import { z } from "zod";
import { money, phone, trimmed, uuid } from "./common";

/**
 * Доставка (`addon.delivery`, ТЗ 09 §9.10): зоны (многоугольник или круг) с минимальным заказом, стоимостью, «бесплатно от»
 * и временем; доставка — к заказу типа «доставка» (позиции — как обычно на кассе); статусы FR-DLV-003; курьеры (роль courier),
 * наличные курьеру и их сдача (BR-195).
 */
export const DELIVERY_STATUSES = ["new", "confirmed", "cooking", "ready", "courier_assigned", "on_the_way", "delivered", "failed", "cancelled", "returned"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];
/** Переходы: диспетчер ведёт до «назначен курьер», курьер — дальше; failed → returned (товар вернулся) */
export const DELIVERY_TRANSITIONS: Record<DeliveryStatus, DeliveryStatus[]> = {
  new: ["confirmed", "cancelled"],
  confirmed: ["cooking", "ready", "cancelled"],
  cooking: ["ready", "cancelled"],
  ready: ["courier_assigned", "delivered", "cancelled"],
  courier_assigned: ["on_the_way", "ready", "cancelled"],
  on_the_way: ["delivered", "failed"],
  failed: ["returned", "on_the_way"],
  delivered: [],
  cancelled: [],
  returned: [],
};
/** Активные — в диспетчерской */
export const DELIVERY_ACTIVE: DeliveryStatus[] = ["new", "confirmed", "cooking", "ready", "courier_assigned", "on_the_way", "failed"];
export const DELIVERY_PAYMENTS = ["prepaid", "cash", "card"] as const;
export type DeliveryPayment = (typeof DELIVERY_PAYMENTS)[number];

const lat = z.number().min(-90).max(90);
const lng = z.number().min(-180).max(180);
export const deliveryZoneSchema = z
  .strictObject({
    name: trimmed(1, 80),
    kind: z.enum(["polygon", "circle"]),
    /** Многоугольник: вершины [lat, lng] по порядку */
    polygon: z.array(z.tuple([lat, lng])).max(200).default([]),
    center: z.strictObject({ lat, lng }).nullable().default(null),
    radiusM: z.number().int().min(50).max(100_000).nullable().default(null),
    minOrder: money.default(0),
    fee: money.default(0),
    /** Бесплатно от суммы заказа; 0 — всегда платно */
    freeFrom: money.default(0),
    etaMin: z.number().int().min(5).max(600).default(60),
    isActive: z.boolean().default(true),
  })
  .refine((z) => z.kind !== "polygon" || z.polygon.length >= 3, { error: "Многоугольник — минимум 3 точки", path: ["polygon"] })
  .refine((z) => z.kind !== "circle" || (!!z.center && !!z.radiusM), { error: "Центр и радиус", path: ["radiusM"] });
export type DeliveryZoneInput = z.input<typeof deliveryZoneSchema>;
export type DeliveryZone = z.output<typeof deliveryZoneSchema>;

/** Расстояние по поверхности Земли, метры (haversine). */
export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6_371_000, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Точка в зоне (AC-DLV-01): граница — внутри; многоугольник — луч, круг — расстояние до центра. */
export function pointInZone(p: { lat: number; lng: number }, zone: Pick<DeliveryZone, "kind" | "polygon" | "center" | "radiusM">): boolean {
  if (zone.kind === "circle") return !!zone.center && !!zone.radiusM && distanceM(p, zone.center) <= zone.radiusM + 1e-6;
  const pts = zone.polygon;
  const eps = 1e-12;
  // На ребре — внутри
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [ay, ax] = pts[j]!, [by, bx] = pts[i]!;
    const cross = (p.lng - ax) * (by - ay) - (p.lat - ay) * (bx - ax);
    if (Math.abs(cross) < eps && p.lng >= Math.min(ax, bx) - eps && p.lng <= Math.max(ax, bx) + eps && p.lat >= Math.min(ay, by) - eps && p.lat <= Math.max(ay, by) + eps) return true;
  }
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [yi, xi] = pts[i]!, [yj, xj] = pts[j]!;
    if (yi > p.lat !== yj > p.lat && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Стоимость доставки: зона — платно, пока подытог меньше «бесплатно от». */
export function deliveryFee(zone: Pick<DeliveryZone, "fee" | "freeFrom">, subtotal: number) {
  return zone.freeFrom > 0 && subtotal >= zone.freeFrom ? 0 : zone.fee;
}

const address = z.strictObject({
  street: trimmed(1, 200),
  entrance: trimmed(0, 20).nullable().optional(),
  floor: trimmed(0, 20).nullable().optional(),
  apartment: trimmed(0, 20).nullable().optional(),
  comment: trimmed(0, 300).nullable().optional(),
});
export const deliveryCreateSchema = z.strictObject({
  /** Заказ типа «доставка»; без него — новый пустой заказ (позиции — на кассе) */
  orderId: uuid.nullable().optional(),
  customerName: trimmed(1, 120),
  phone,
  customerId: uuid.nullable().optional(),
  address,
  lat: lat.nullable().optional(),
  lng: lng.nullable().optional(),
  /** Зона: по координатам определяется сама; без координат — выбрать */
  zoneId: uuid.nullable().optional(),
  /** Стоимость вручную (без зоны) */
  fee: money.optional(),
  /** Ко времени; пусто — как можно скорее */
  scheduledAt: z.iso.datetime({ offset: true }).nullable().optional(),
  payment: z.enum(DELIVERY_PAYMENTS).default("cash"),
  /** Сдача с купюры (наличные курьеру) */
  changeFrom: money.nullable().optional(),
});
export type DeliveryCreateInput = z.input<typeof deliveryCreateSchema>;
export const deliveryUpdateSchema = deliveryCreateSchema.omit({ orderId: true }).partial().extend({ version: z.number().int().min(1) });
export const deliveryStatusSchema = z.strictObject({ status: z.enum(DELIVERY_STATUSES), reason: trimmed(1, 300).optional() });
export const deliveryAssignSchema = z.strictObject({ courierId: uuid.nullable() });
export const deliveryLocationSchema = z.strictObject({ lat, lng });
export const deliveriesQuery = z.object({
  status: z.enum(["active", "done", "all"]).default("active"),
  courierId: uuid.optional(),
  from: z.iso.date().optional(), to: z.iso.date().optional(),
});
export const courierHandoverSchema = z.strictObject({ courierId: uuid });
